import React, { useEffect, useState } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { SidebarProvider } from '@/components/ui/sidebar'
import AppSidebar from '@/components/layout/AppSidebar'
import Landing from '@/pages/LandingModern'
import ChurchRegistrationForm from '@/components/ChurchRegistrationForm'
import Dashboard from '@/pages/Dashboard'
import MembersNew from '@/pages/MembersNew'
import Events from '@/pages/Events'
import Devotions from '@/pages/Devotions'
import ReadingPlans from '@/pages/ReadingPlans'
import Communications from '@/pages/Communications'
import Settings from '@/pages/Settings'
import Profile from '@/pages/Profile'
import AdminManagement from '@/pages/AdminManagement'
import PrayerRequests from '@/pages/PrayerRequests'
import FastingManagement from '@/pages/FastingManagement'
import Testimonies from '@/pages/Testimonies'
import Counseling from '@/pages/Counseling'
import Evangelism from '@/pages/Evangelism'
import LoginNew from '@/pages/LoginNew'
import Verify2FA from '@/pages/Verify2FA'
import ResetPassword from '@/pages/ResetPassword'

// Finance Pages
import FinanceDashboard from '@/pages/finance/FinanceDashboard'
import Contributions from '@/pages/finance/Contributions'
import Expenses from '@/pages/finance/Expenses'
import FinancialReports from '@/pages/finance/FinancialReports'
import ChartOfAccounts from '@/pages/finance/ChartOfAccounts'
import JournalEntries from '@/pages/finance/JournalEntries'
import Budgets from '@/pages/finance/Budgets'
// Temporarily disabled for deployment - missing dependencies
// import BankReconciliation from '@/pages/finance/BankReconciliation'
import AuditLog from '@/pages/finance/AuditLog'
import Vendors from '@/pages/finance/Vendors'
import FixedAssets from '@/pages/finance/FixedAssets'
import PostDepreciation from '@/pages/finance/PostDepreciation'
import AssetReports from '@/pages/finance/AssetReports'
import FixedAssetsUnified from '@/pages/finance/FixedAssetsUnified'

// HR Pages
import Employees from '@/pages/hr/Employees'
import PayrollProcessing from '@/pages/hr/PayrollProcessing'
import StatutoryReports from '@/pages/hr/StatutoryReports'

// Attendance Pages
import Services from '@/pages/attendance/Services'
import AttendanceDashboard from '@/pages/attendance/AttendanceDashboard'
import WelfareChecks from '@/pages/attendance/WelfareChecks'

// Demo Pages
import BibleVerseLinkDemo from '@/pages/BibleVerseLinkDemo'

// Media Library
import MediaLibrary from '@/pages/MediaLibrary'

// Pricing & Subscription
import Pricing from '@/pages/Pricing'
import Checkout from '@/pages/Checkout'
import HelpSupport from '@/pages/HelpSupport'
import KnowledgeBase from '@/pages/KnowledgeBase'

// Announcements
import Announcements from '@/pages/Announcements'

// Platform Admin Pages
import PlatformDashboard from '@/pages/platform/PlatformDashboard'
import ChurchManagement from '@/pages/platform/ChurchManagement'
import ChurchesList from '@/pages/platform/ChurchesList'
import ChurchInvitations from '@/pages/platform/ChurchInvitations'
import SubscriptionManagement from '@/pages/platform/SubscriptionManagement'
import PaymentHistory from '@/pages/platform/PaymentHistory'
import SystemHealth from '@/pages/platform/SystemHealth'
import PlatformAuditLog from '@/pages/platform/PlatformAuditLog'
import ErrorTracking from '@/pages/platform/ErrorTracking'
import SupportTickets from '@/pages/platform/SupportTickets'

import { supabase } from '@/lib/supabase'
import { validateSession, logout, AdminUser } from '@/lib/authService'
import { useChurchLanguage } from '@/hooks/useChurchLanguage'
import { ImpersonationProvider } from '@/contexts/ImpersonationContext'
import ImpersonationBanner from '@/components/ImpersonationBanner'
import { DemoModeProvider } from '@/contexts/DemoModeContext'
import { DemoModeBanner } from '@/components/DemoModeToggle'
import SubscriptionBanner from '@/components/SubscriptionBanner'
import FeatureGate from '@/components/FeatureGate'

type AuthView = 'login' | '2fa' | 'authenticated'

function App() {
  const navigate = useNavigate()
  const [authView, setAuthView] = useState<AuthView>('login')
  const [isLoading, setIsLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null)
  const [twoFactorToken, setTwoFactorToken] = useState<string>('')
  const [showRegistrationForm, setShowRegistrationForm] = useState(false)

  // Load and apply church language preference
  useChurchLanguage()

  useEffect(() => {
    // Check for saved theme preference, default to dark mode
    const savedTheme = localStorage.getItem('theme') || 'dark'
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    // Detect password recovery flow from URL hash (Supabase redirects with #type=recovery)
    const hash = window.location.hash
    if (hash && hash.includes('type=recovery')) {
      navigate('/reset-password' + hash)
      return
    }

    // Check for existing session
    checkSession()
  }, [])

  async function checkSession() {
    try {
      // Validate session with Supabase Auth
      const user = await validateSession()

      if (!user) {
        setIsLoading(false)
        setAuthView('login')
        return
      }

      setCurrentUser(user)
      setAuthView('authenticated')
    } catch (error) {
      console.error('Session check error:', error)
      setAuthView('login')
    } finally {
      setIsLoading(false)
    }
  }

  function handleLogin(user: AdminUser) {
    console.log('🚀 [APP] handleLogin called with user:', {
      id: user.id,
      email: user.email,
      role: user.role,
      churchId: user.churchId
    })
    setCurrentUser(user)
    console.log('🚀 [APP] setCurrentUser completed')
    setAuthView('authenticated')
    console.log('🚀 [APP] setAuthView to authenticated - navigating to dashboard')

    // Redirect to dashboard
    navigate('/dashboard')
    console.log('🚀 [APP] Navigate to /dashboard completed')
  }

  function handle2FARequired(twoFactorToken: string, userInfo: Partial<AdminUser>) {
    setTwoFactorToken(twoFactorToken)
    setAuthView('2fa')
  }

  function handle2FAVerified(user: AdminUser) {
    setCurrentUser(user)
    setAuthView('authenticated')
  }

  function handleBackToLogin() {
    setTwoFactorToken('')
    setAuthView('login')
  }

  async function handleLogout() {
    // Sign out via Supabase Auth
    await logout()

    setAuthView('login')
    setCurrentUser(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Show landing page for unauthenticated users, unless on /login, /verify-2fa, or /reset-password
  if (window.location.pathname === '/login') {
    return <LoginNew onLogin={handleLogin} on2FARequired={handle2FARequired} />
  }

  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />
  }

  if (authView === '2fa') {
    return (
      <Verify2FA
        twoFactorToken={twoFactorToken}
        onVerified={handle2FAVerified}
        onBack={handleBackToLogin}
      />
    )
  }

  // If not authenticated and not on login page, show landing page
  if (authView === 'login') {
    return (
      <>
        <Landing onGetStarted={() => setShowRegistrationForm(true)} />
        <ChurchRegistrationForm
          open={showRegistrationForm}
          onClose={() => setShowRegistrationForm(false)}
        />
      </>
    )
  }

  // Authenticated users see the admin dashboard
  return (
    <DemoModeProvider>
      <ImpersonationProvider user={currentUser}>
        <SidebarProvider>
          <div className="flex h-screen w-full">
            <AppSidebar user={currentUser} onLogout={handleLogout} />
            <div className="flex-1 flex flex-col overflow-hidden">
              <DemoModeBanner />
              <ImpersonationBanner />
              <SubscriptionBanner churchId={currentUser?.churchId} />
              <main className="flex-1 overflow-auto">
          <Routes>
            <Route
              path="/dashboard"
              element={
                currentUser?.adminLevel === 'super_admin' ?
                  <PlatformDashboard user={currentUser} /> :
                  <Dashboard user={currentUser} />
              }
            />
            <Route
              index
              element={
                currentUser?.adminLevel === 'super_admin' ?
                  <PlatformDashboard user={currentUser} /> :
                  <Dashboard user={currentUser} />
              }
            />
            <Route path="members" element={<MembersNew />} />
            <Route path="events" element={<Events user={currentUser} />} />
            <Route path="devotions" element={<Devotions user={currentUser} />} />
            <Route path="reading-plans" element={<ReadingPlans user={currentUser} />} />
            <Route path="prayer-requests" element={<PrayerRequests user={currentUser} />} />
            <Route path="fasting" element={<FastingManagement user={currentUser} />} />
            <Route path="testimonies" element={<Testimonies user={currentUser} />} />
            <Route path="counseling" element={<Counseling user={currentUser} />} />
            <Route path="evangelism" element={<Evangelism user={currentUser} />} />
            <Route path="communications" element={<Communications user={currentUser} />} />

            {/* Finance Routes */}
            <Route path="finance">
              <Route index element={<FeatureGate feature="finance" churchId={currentUser?.churchId}><FinanceDashboard user={currentUser} /></FeatureGate>} />
              <Route path="contributions" element={<FeatureGate feature="finance" churchId={currentUser?.churchId}><Contributions user={currentUser} /></FeatureGate>} />
              <Route path="expenses" element={<FeatureGate feature="finance" churchId={currentUser?.churchId}><Expenses user={currentUser} /></FeatureGate>} />
              <Route path="reports" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><FinancialReports user={currentUser} /></FeatureGate>} />
              <Route path="accounts" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><ChartOfAccounts user={currentUser} /></FeatureGate>} />
              <Route path="journal" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><JournalEntries user={currentUser} /></FeatureGate>} />
              <Route path="budgets" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><Budgets user={currentUser} /></FeatureGate>} />
              {/* Temporarily disabled for deployment - missing dependencies */}
              {/* <Route path="reconciliation" element={<BankReconciliation user={currentUser} />} /> */}
              <Route path="vendors" element={<FeatureGate feature="finance" churchId={currentUser?.churchId}><Vendors user={currentUser} /></FeatureGate>} />

              {/* Fixed Assets - Unified Interface (NEW - Recommended) */}
              <Route path="fixed-assets" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><FixedAssetsUnified user={currentUser} /></FeatureGate>} />

              {/* Fixed Assets - Legacy Routes (Keep for compatibility & manual backup) */}
              <Route path="assets" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><FixedAssets user={currentUser} /></FeatureGate>} />
              <Route path="depreciation" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><PostDepreciation user={currentUser} /></FeatureGate>} />
              <Route path="asset-reports" element={<FeatureGate feature="completeFinance" churchId={currentUser?.churchId}><AssetReports user={currentUser} /></FeatureGate>} />

              <Route path="audit-log" element={<FeatureGate feature="finance" churchId={currentUser?.churchId}><AuditLog user={currentUser} /></FeatureGate>} />
            </Route>

            {/* HR Routes */}
            <Route path="hr">
              <Route path="employees" element={<FeatureGate feature="hrPayroll" churchId={currentUser?.churchId}><Employees user={currentUser} /></FeatureGate>} />
              <Route path="payroll" element={<FeatureGate feature="hrPayroll" churchId={currentUser?.churchId}><PayrollProcessing user={currentUser} /></FeatureGate>} />
              <Route path="statutory-reports" element={<FeatureGate feature="hrPayroll" churchId={currentUser?.churchId}><StatutoryReports user={currentUser} /></FeatureGate>} />
            </Route>

            {/* Attendance Routes */}
            <Route path="attendance">
              <Route index element={<FeatureGate feature="attendance" churchId={currentUser?.churchId}><AttendanceDashboard /></FeatureGate>} />
              <Route path="services" element={<FeatureGate feature="attendance" churchId={currentUser?.churchId}><Services user={currentUser} /></FeatureGate>} />
              <Route path="dashboard" element={<FeatureGate feature="attendance" churchId={currentUser?.churchId}><AttendanceDashboard /></FeatureGate>} />
              <Route path="welfare" element={<FeatureGate feature="attendance" churchId={currentUser?.churchId}><WelfareChecks /></FeatureGate>} />
            </Route>

            {/* Platform Admin Routes */}
            <Route path="platform">
              <Route index element={<PlatformDashboard user={currentUser} />} />
              <Route path="health" element={<SystemHealth user={currentUser} />} />
              <Route path="audit-log" element={<PlatformAuditLog user={currentUser} />} />
              <Route path="errors" element={<ErrorTracking user={currentUser} />} />
              <Route path="support" element={<SupportTickets user={currentUser} />} />
              <Route path="churches" element={<ChurchesList user={currentUser} />} />
              <Route path="churches/:churchId" element={<ChurchManagement user={currentUser} />} />
              <Route path="invitations" element={<ChurchInvitations user={currentUser} />} />
              <Route path="subscriptions" element={<SubscriptionManagement user={currentUser} />} />
              <Route path="payments" element={<PaymentHistory user={currentUser} />} />
            </Route>

            {/* Demo Routes */}
            <Route path="demo/bible-verse-link" element={<BibleVerseLinkDemo />} />

            {/* Announcements */}
            <Route path="announcements" element={<Announcements user={currentUser} />} />

            {/* Media Library */}
            <Route path="media" element={<FeatureGate feature="mediaLibrary" churchId={currentUser?.churchId}><MediaLibrary user={currentUser} /></FeatureGate>} />

            {/* Pricing & Subscription */}
            <Route path="pricing" element={<Pricing user={currentUser} />} />
            <Route path="checkout" element={<Checkout user={currentUser} />} />

            {/* Help & Support */}
            <Route path="support" element={<HelpSupport user={currentUser} />} />

            {/* Knowledge Base */}
            <Route path="knowledge" element={<KnowledgeBase user={currentUser} />} />

            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<Profile user={currentUser} />} />
            <Route path="admins" element={<AdminManagement user={currentUser} />} />
          </Routes>
              </main>
            </div>
          </div>
        </SidebarProvider>
      </ImpersonationProvider>
    </DemoModeProvider>
  )
}

export default App